import React, {useCallback, useEffect, useRef, useState} from "react";
import {ColumnFilterProps} from "./types";
import {apiV2} from "@/api/axiosConfig";
import {PaginatedResponse} from "@/types/PaginatedResponse";
import {SearchInput} from "@/components/search-input";
import {useQuery} from "react-query";
import {useDebounce} from "use-debounce";
import {useDropdownPosition} from "@/hooks/useDropdownPosition";
import {ButtonV2} from "@/components/ui/ButtonsV2/ButtonV2";

type IncidentFeed = {
    id: string;
    name: string;
    isDefault?: boolean;
    type?: "intelligence_fusion" | "samdesk";
}

const fetchIncidentFeeds = async (search: string): Promise<IncidentFeed[]> => {
    const response = await apiV2.get<PaginatedResponse<IncidentFeed>>("incident-feeds", {
        params: {
            page: 1,
            limit: 50,
            search: search || undefined,
        }
    });
    return response.data.items || [];
};

export const IncidentFeedFilter: React.FC<ColumnFilterProps & { multiSelect?: boolean }> = ({label, onChange, filterParams, multiSelect = false}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [debouncedSearch] = useDebounce(search, 300);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const position = useDropdownPosition(isOpen, dropdownRef, 300);

    // Initialize selectedFeeds from filterParams
    const getInitialFeeds = useCallback((): IncidentFeed[] => {
        if (filterParams.feedIDs && Array.isArray(filterParams.feedIDs) && filterParams.feedIDs.length > 0) {
            const feedIDs = filterParams.feedIDs;
            const feedNames = Array.isArray(filterParams.feedNames) ? filterParams.feedNames : [];
            return feedIDs.map((id, index) => ({
                id,
                name: feedNames[index] || id
            }));
        }
        return [];
    }, [filterParams.feedIDs, filterParams.feedNames]);

    // For backwards compatibility with single select
    const getInitialFeed = useCallback((): IncidentFeed | null => {
        const feeds = getInitialFeeds();
        return feeds.length > 0 ? feeds[0] : null;
    }, [getInitialFeeds]);

    // Applied state (what's currently in the filter)
    const [appliedFeeds, setAppliedFeeds] = useState<IncidentFeed[]>(getInitialFeeds);
    const [appliedFeed, setAppliedFeed] = useState<IncidentFeed | null>(getInitialFeed);

    // Staged state (what the user is selecting)
    const [selectedFeeds, setSelectedFeeds] = useState<IncidentFeed[]>(getInitialFeeds);
    const [selectedFeed, setSelectedFeed] = useState<IncidentFeed | null>(getInitialFeed);

    const searchInputRef = useRef<HTMLInputElement>(null);
    const hasLoadedDefaultRef = useRef(false);

    // Use react-query to fetch and cache feeds
    const {data: feeds = [], isLoading: loading} = useQuery(
        ["incident-feeds", debouncedSearch],
        () => fetchIncidentFeeds(debouncedSearch),
        {
            enabled: isOpen,
            staleTime: 5 * 60 * 1000,
            cacheTime: 10 * 60 * 1000,
        }
    );

    const handleToggle = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isOpen) {
            // When opening, sync staged state with applied state
            if (multiSelect) {
                setSelectedFeeds(appliedFeeds);
            } else {
                setSelectedFeed(appliedFeed);
            }
        }
        setIsOpen(!isOpen);
    }, [isOpen, appliedFeed, appliedFeeds, multiSelect]);

    const handleSelectFeed = useCallback((feed: IncidentFeed, e: React.MouseEvent) => {
        e.stopPropagation();

        if (multiSelect) {
            // Multi-select mode: toggle feed in array
            setSelectedFeeds(prev => {
                const isSelected = prev.some(f => f.id === feed.id);
                if (isSelected) {
                    return prev.filter(f => f.id !== feed.id);
                } else {
                    return [...prev, feed];
                }
            });
        } else {
            // Single-select mode: if clicking the already selected feed, deselect it
            setSelectedFeed(prev => {
                if (prev?.id === feed.id) {
                    return null;
                }
                return feed;
            });
        }
    }, [multiSelect]);

    const handleApply = useCallback(() => {
        if (multiSelect) {
            // Multi-select mode
            setAppliedFeeds(selectedFeeds);

            if (selectedFeeds.length > 0) {
                onChange({
                    feedIDs: selectedFeeds.map(f => f.id),
                    feedNames: selectedFeeds.map(f => f.name),
                    feedType: selectedFeeds.length > 0 ? selectedFeeds[0].type : undefined
                });
            } else {
                onChange({
                    feedIDs: undefined,
                    feedNames: undefined,
                    feedType: undefined
                });
            }
        } else {
            // Single-select mode
            setAppliedFeed(selectedFeed);

            if (selectedFeed) {
                onChange({
                    feedIDs: [selectedFeed.id],
                    feedNames: [selectedFeed.name],
                    feedType: selectedFeed.type
                });
            } else {
                onChange({
                    feedIDs: undefined,
                    feedNames: undefined,
                    feedType: undefined
                });
            }
        }
        setIsOpen(false);
    }, [selectedFeed, selectedFeeds, onChange, multiSelect]);

    const handleClear = useCallback(() => {
        setSelectedFeed(null);
        setAppliedFeed(null);
        setSelectedFeeds([]);
        setAppliedFeeds([]);
        onChange({
            feedIDs: undefined,
            feedNames: undefined,
            feedType: undefined
        });
    }, [onChange]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearch("");
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 0);
        }
    }, [isOpen]);

    // Update feed states when filterParams change (e.g., from URL)
    useEffect(() => {
        if (multiSelect) {
            const newFeeds = getInitialFeeds();
            setAppliedFeeds(newFeeds);
            setSelectedFeeds(newFeeds);
        } else {
            const newFeed = getInitialFeed();
            setAppliedFeed(newFeed);
            setSelectedFeed(newFeed);
        }
    }, [getInitialFeed, getInitialFeeds, multiSelect]);

    const isFeedSelected = useCallback((feedId: string) => {
        if (multiSelect) {
            return selectedFeeds.some(f => f.id === feedId);
        }
        return selectedFeed?.id === feedId;
    }, [selectedFeed, selectedFeeds, multiSelect]);

    // Auto-select default feed on mount if no feed is selected (only for single-select)
    useEffect(() => {
        // Only run once and only if no feed is selected, and only in single-select mode
        if (multiSelect) return; // Skip for multi-select
        if (hasLoadedDefaultRef.current || appliedFeed) return;

        const loadDefaultFeed = async () => {
            try {
                const allFeeds = await fetchIncidentFeeds("");
                const defaultFeed = allFeeds.find(feed => feed.isDefault === true);

                if (defaultFeed) {
                    setSelectedFeed(defaultFeed);
                    setAppliedFeed(defaultFeed);
                    // Automatically apply the default feed
                    onChange({
                        feedIDs: [defaultFeed.id],
                        feedNames: [defaultFeed.name],
                        feedType: defaultFeed.type
                    });
                }
            } catch (error) {
                console.error("Failed to load default feed:", error);
            } finally {
                hasLoadedDefaultRef.current = true;
            }
        };

        loadDefaultFeed();
    }, [appliedFeed, onChange, multiSelect]);

    return (
        <div className="relative block" ref={dropdownRef}>
            <button
                onClick={handleToggle}
                className="flex items-center gap-1 hover:text-blue-600 transition-colors font-bold"
            >
                <span>{label}</span>
                {multiSelect ? (
                    appliedFeeds.length > 0 && (
                        <span className="text-xs text-blue-600 font-normal">
                            ({appliedFeeds.length} selected)
                        </span>
                    )
                ) : (
                    appliedFeed && (
                        <span className="text-xs text-blue-600 font-normal">
                            ({appliedFeed.name})
                        </span>
                    )
                )}
                <span className={`text-xs transition-transform ${isOpen ? "rotate-180" : ""}`}>▼</span>
            </button>

            {isOpen && (
                <div className={`absolute top-full ${position === "left" ? "left-0" : "right-0"} mt-1 bg-white rounded shadow-lg z-50 w-[300px] flex flex-col max-h-[450px]`} onClick={(e) => e.stopPropagation()}>
                    {/* Header with Search */}
                    <div className="p-3">
                        <label className="font-medium block mb-2">Filter by Feed</label>
                        <SearchInput
                            ref={searchInputRef}
                            initialValue={search}
                            onChange={setSearch}
                        />
                    </div>

                    {/* Content Area */}
                    <div className="flex-1 overflow-y-auto">
                        {loading ? (
                            <div className="px-4 py-2 text-gray-500 text-center font-normal">
                                Loading...
                            </div>
                        ) : feeds.length === 0 ? (
                            <div className="px-4 py-2 text-gray-500 text-center font-normal">
                                No feeds found
                            </div>
                        ) : (
                            feeds.map((feed) => (
                                <button
                                    key={feed.id}
                                    onClick={(e) => handleSelectFeed(feed, e)}
                                    className={`w-full text-left px-4 py-2 hover:bg-gray-100 font-normal flex items-center gap-2 ${isFeedSelected(feed.id) ? "bg-blue-50" : ""}`}
                                >
                                    <input
                                        type={multiSelect ? "checkbox" : "radio"}
                                        checked={isFeedSelected(feed.id)}
                                        onChange={() => {}} // Handled by button onClick
                                        className="w-4 h-4 accent-blue"
                                    />
                                    <span>{feed.name}</span>
                                </button>
                            ))
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="p-3 flex gap-2">
                        <ButtonV2 label="Clear" color="gray" onClick={handleClear} />
                        <ButtonV2 label="Apply Filter" color="blue" onClick={handleApply} />
                    </div>
                </div>
            )}
        </div>
    );
};
